import React from 'react';
import { Dimensions, StyleSheet } from 'react-native';

import { BigButton } from '@components/Button';
import IconButton from '@components/IconButton';
import Modal, { ModalProps } from '@components/Modal';
import Surface from '@components/Surface';
import { PrimaryTitle } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { differenceInHours } from '@helpers/Dates';
import {
  getLastCreatePlanPromptTimestamp,
  setLastCreatePlanPromptTimestamp,
} from '@helpers/LocalStorage';
import { logError } from '@helpers/Loggers';
import { RuntimeConfig } from '@helpers/RuntimeConfig';

const NUM_HOURS_BETWEEN_PROMPTS = 24;

type CreatePlanPromptModalProps = Omit<ModalProps, 'children' | 'visible'> & {
  /** Called when the prompt's create-plan action is selected. */
  onCreatePlanPress: () => void;
};

function isTimeForPrompt(lastDate: Date) {
  const now = new Date(Date.now());
  const diff = differenceInHours(now, lastDate);
  return diff >= NUM_HOURS_BETWEEN_PROMPTS;
}

export function CreatePlanPromptModal({ onCreatePlanPress, ...props }: CreatePlanPromptModalProps) {
  const [visible, setVisible] = React.useState(false);
  const appTheme = useAppTheme();
  const disabled = RuntimeConfig.disableCreatePlanPrompt;

  const hideModal = () => setVisible(false);

  const maybeShowModal = React.useCallback(async () => {
    try {
      const lastTime = await getLastCreatePlanPromptTimestamp();
      if (!lastTime || isTimeForPrompt(lastTime)) {
        setVisible(true);
        await setLastCreatePlanPromptTimestamp();
      }
    } catch (error) {
      logError(error);
    }
  }, []);

  React.useEffect(() => {
    if (disabled) return;

    void Promise.resolve().then(maybeShowModal);
  }, [disabled, maybeShowModal]);

  const onPress = () => {
    onCreatePlanPress();
    hideModal();
  };

  if (disabled) return null;

  return (
    <Modal
      visible={visible}
      contentContainerStyle={styles.modalContent}
      onDismiss={hideModal}
      {...props}>
      <Surface
        style={[
          styles.container,
          { width: 0.9 * Math.min(Dimensions.get('window').width, appTheme.maxWidth) },
        ]}>
        <IconButton
          icon="close"
          style={styles.closeButton}
          onPress={hideModal}
          testID="components.CreatePlanPrompt.IconButton.Dismiss"
        />
        <PrimaryTitle style={styles.title}>Where are you going?</PrimaryTitle>
        <BigButton
          mode="contained"
          onPress={onPress}
          style={styles.button}
          testID="components.CreatePlanPrompt.Button">
          Add A Plan
        </BigButton>
      </Surface>
    </Modal>
  );
}

const styles = StyleSheet.create({
  button: {
    alignSelf: 'center',
    width: '80%',
  },
  closeButton: {
    position: 'absolute',
    right: -5,
    top: -5,
    zIndex: 1,
  },
  container: {
    alignSelf: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    alignSelf: 'center',
  },
  title: {
    alignSelf: 'center',
    paddingVertical: 10,
    textAlign: 'center',
  },
});
